import { useContext, useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { UserContext } from "../auth";
import { IntakeSubmitProvider } from "../state/IntakeSubmitContext";
import { useIntakeSubmitContext } from "../state/useIntakeSubmitContext";
import { IntakeStatusPill } from "../state/IntakeStatusPill";
import styles from "./AppLayout.module.css";

interface NavItem {
  id: string;
  label: string;
  to: string;
  disabled?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: "new", label: "New project", to: "/new" },
  { id: "bulk", label: "Bulk import", to: "/bulk" },
  { id: "operations", label: "Operations", to: "/operations" },
  { id: "agents", label: "Agents", to: "/agents" },
];

const TITLE_BY_PATH: Record<string, { title: string; subtitle: string }> = {
  "/new": {
    title: "New project",
    subtitle:
      "Submit a project, watch the agent route + reason, get a structured decision back.",
  },
  "/bulk": {
    title: "Bulk import",
    subtitle:
      "Upload your multi-site tracker; one project per row lands in Operations as queued.",
  },
  "/operations": {
    title: "Operations",
    subtitle:
      "Every project on the platform — pre-development, construction, operation, decommissioning.",
  },
  "/agents": {
    title: "Agents",
    subtitle:
      "The InRange delivery org as a roster of agents — three built, eight planned.",
  },
};

const RUN_HEAD = {
  title: "Run detail",
  subtitle:
    "What this run analysed, decided, and left outstanding — plus the full project state at the end.",
};

const PROJECT_DETAILS_HEAD = {
  title: "Project details",
  subtitle:
    "Every tracker column for one project — fill in what the bulk import left sparse. Saving here records metadata only; it does not submit an agent run.",
};

const headForPath = (pathname: string) => {
  if (pathname.startsWith("/runs/")) {
    return RUN_HEAD;
  }
  if (pathname.startsWith("/projects/")) {
    return PROJECT_DETAILS_HEAD;
  }
  return TITLE_BY_PATH[pathname] ?? TITLE_BY_PATH["/operations"];
};

// Inner layout body — needs to live inside IntakeSubmitProvider so it
// can read the intake state for the auto-navigate effect + render the
// floating pill. Split from `AppLayout` so the provider can wrap it.
const AppLayoutBody = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const head = headForPath(pathname);
  const { user, logout } = useContext(UserContext);
  const { state, dismiss } = useIntakeSubmitContext();

  // Auto-navigate to the run when Atlas finishes, regardless of
  // which page the operator is on. Once we've navigated for a given
  // run_id we never re-fire — without the ref guard, hopping from
  // the run page to Agents and back would yank the operator
  // straight back to the run on every pathname change for as long
  // as the pill's "done" state lingers (the operator can't dismiss
  // it fast enough). The ref resets whenever a fresh submit
  // starts, so two consecutive submits both navigate exactly once.
  const navigatedRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Reset the "we've navigated for this one" marker the moment a
    // new submit kicks off, so the next "done" gets the redirect.
    if (state.stage === "uploading" || state.stage === "atlas") {
      navigatedRunIdRef.current = null;
    }
  }, [state.stage]);
  useEffect(() => {
    if (state.stage !== "done" || !state.result) return;
    const { project_id, run_id } = state.result;
    if (!run_id) return;
    if (navigatedRunIdRef.current === run_id) return;
    const target = `/runs/${encodeURIComponent(project_id)}/${encodeURIComponent(run_id)}`;
    // Skip if the operator is already on this run page OR queueing
    // another submit on /new — but still mark navigated so we don't
    // chase them later if they leave + come back.
    navigatedRunIdRef.current = run_id;
    if (pathname === target || pathname === "/new") return;
    navigate(target);
    void dismiss; // referenced for lint
  }, [state.stage, state.result, pathname, navigate, dismiss]);

  return (
    <>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>InRange Agents</div>
        <nav className={styles.nav}>
          {NAV_ITEMS.map((it) => (
            <NavLink
              key={it.id}
              to={it.to}
              className={({ isActive }) =>
                `${styles.item} ${isActive ? styles.itemActive : ""}`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className={styles.foot}>
          <div>{user?.email ?? "Signed in"}</div>
          {logout && (
            <button
              type="button"
              onClick={() => logout()}
              className={styles.logoutButton}
            >
              Log out
            </button>
          )}
        </div>
      </aside>
      <main className={styles.main}>
        <header className={styles.topBar}>
          <h1 className={styles.title}>{head.title}</h1>
          <div className={styles.subtitle}>{head.subtitle}</div>
        </header>
        <div className={styles.liveBanner}>
          <span className={styles.liveDot} />
          <span>
            Connected to <code>inrange-agent-devjohn</code> · Sonnet 4.6 via Bedrock
            (eu-west-1)
          </span>
        </div>
        <div className={styles.scroll}>
          <Outlet />
        </div>
      </main>
      <IntakeStatusPill />
    </>
  );
};

export const AppLayout = () => (
  <IntakeSubmitProvider>
    <AppLayoutBody />
  </IntakeSubmitProvider>
);
